import { Badge, Card, Group, Skeleton, Stack, Text, Title } from '@mantine/core'
import { IconLayoutList } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'

import {
  type AdminRedeemable,
  useAdminRedeemables,
} from '@repo/common/queries/admin/redeem'

import { IconRedeem } from '@/components/icons/IconRedeem.tsx'
import { TowerCard } from '@/components/ui/TowerCard'

import styles from './RedeemablesOverview.module.css'

interface RedeemablePanelProps {
  redeemable: AdminRedeemable
}

const RedeemablePanel: React.FC<RedeemablePanelProps> = ({ redeemable }) => {
  return (
    <Card withBorder radius="md" p={0} className={styles.panel}>
      <Link
        to="/redeem/$id"
        params={{ id: redeemable.id }}
        className={styles.panel_link}
      >
        <div className={styles.panel_content}>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap="sm" style={{ flex: 1 }}>
              <Group align="center" gap="xs" className={styles.panel_header}>
                <Title order={5} className={styles.panel_name}>
                  {redeemable.name}
                </Title>
                <Badge variant="light" size="xs" className={styles.id_badge}>
                  {redeemable.id}
                </Badge>
                {redeemable.published && (
                  <Badge
                    variant="light"
                    size="xs"
                    className={styles.status_published}
                  >
                    Published
                  </Badge>
                )}
                {redeemable.paused && (
                  <Badge
                    variant="light"
                    size="xs"
                    className={styles.status_paused}
                  >
                    Paused
                  </Badge>
                )}
              </Group>

              <Group gap="lg" className={styles.metrics_row}>
                <div className={styles.metric_item}>
                  <Text className={styles.metric_label}>Total</Text>
                  <Text className={styles.metric_value}>
                    {redeemable.total_redemptions}
                  </Text>
                </div>
                <div className={styles.metric_item}>
                  <Text className={styles.metric_label}>Last 24h</Text>
                  <Text className={styles.metric_value}>
                    {redeemable.redemptions_last_24h}
                  </Text>
                </div>
                <div className={styles.metric_item}>
                  <Text className={styles.metric_label}>Non-Success 24h</Text>
                  <Text
                    className={`${styles.metric_value} ${
                      redeemable.non_success_redemptions_last_24h > 0
                        ? styles.metric_warning
                        : ''
                    }`}
                  >
                    {redeemable.non_success_redemptions_last_24h}
                  </Text>
                </div>
                <div className={styles.metric_item}>
                  <Text className={styles.metric_label}>In Flight</Text>
                  <Text
                    className={`${styles.metric_value} ${
                      redeemable.in_flight_redemptions > 0
                        ? styles.metric_active
                        : ''
                    }`}
                  >
                    {redeemable.in_flight_redemptions}
                  </Text>
                </div>
              </Group>

              <Group gap="md" className={styles.secondary_info}>
                <Text size="xs" c="dimmed">
                  Created {dayjs(redeemable.created_at).format('MMM D, YYYY')}
                </Text>
                <Text size="xs" c="dimmed">
                  Price Feed: {redeemable.price_feed_protocol}
                  {redeemable.price_feed_network
                    ? ` (${redeemable.price_feed_network})`
                    : ''}
                </Text>
              </Group>
            </Stack>
          </Group>
        </div>
      </Link>
    </Card>
  )
}

const LoadingSkeleton: React.FC = () => (
  <Card withBorder radius="md" p="md">
    <Group justify="space-between" align="center" wrap="nowrap">
      <Group gap="md" align="center">
        <Group gap="xs">
          <Skeleton height={20} width={150} />
          <Skeleton height={16} width={40} />
        </Group>
        <Skeleton height={12} width={60} />
        <Skeleton height={12} width={80} />
        <Skeleton height={12} width={100} />
      </Group>
    </Group>
  </Card>
)

export const RedeemablesOverview: React.FC = () => {
  const { data, isLoading, error } = useAdminRedeemables()

  if (error) {
    return (
      <TowerCard
        title="Redeemables"
        description="Error loading redeemables"
        icon={<IconRedeem size={24} />}
      >
        <Text c="red">Failed to load redeemables data</Text>
      </TowerCard>
    )
  }

  const redeemables = data?.redeemables.nodes ?? []

  return (
    <TowerCard
      title="Redeemables"
      description="All redeemable assets and their redemption activity"
      icon={<IconLayoutList size={32} />}
    >
      <Stack gap="lg">
        {isLoading ? (
          <Stack gap="lg">
            {Array.from({ length: 3 }).map((_, index) => (
              <LoadingSkeleton key={index} />
            ))}
          </Stack>
        ) : redeemables.length === 0 ? (
          <Text ta="center" c="dimmed" py="xl">
            No redeemables found
          </Text>
        ) : (
          <Stack gap="lg">
            {redeemables.map((redeemable) => (
              <RedeemablePanel key={redeemable.id} redeemable={redeemable} />
            ))}
          </Stack>
        )}

        {!isLoading && redeemables.length > 0 && (
          <Text size="sm" c="dimmed" ta="center">
            Showing {redeemables.length} of {data?.redeemables.total_count ?? 0}{' '}
            redeemables
          </Text>
        )}
      </Stack>
    </TowerCard>
  )
}
